import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
} from '@mui/material';
import {
  CorporateCharterScheduleSchemaType,
  LineDetailsSchemaType,
} from 'components/forms/CorporateCharter/types';
import { UseFormReturn } from 'react-hook-form';
import { colors, ControlledField, DistanceUnit } from 'remote/global';
import { useOperatorCurrency, useOperatorDistanceUnit } from '@/api/queries/users';
import { convertDistanceUnit } from 'utils/helpers';

const getTotalDistance = (
  itinerary: CorporateCharterScheduleSchemaType,
  distanceUnit: DistanceUnit,
  index?: number
) => {
  const totalDistanceKm = itinerary?.stops.reduce((acc, { distanceKmFromPrevious }, i: number) => {
    if (index && i >= index) return acc;
    return acc + distanceKmFromPrevious;
  }, 0);
  return parseFloat(convertDistanceUnit(totalDistanceKm, DistanceUnit.KM, distanceUnit).toFixed(2));
};

const getMinutesFromPrevious = (itinerary: CorporateCharterScheduleSchemaType, index?: number) => {
  const totalTime = itinerary?.stops.reduce((acc, { minutesFromPrevious }, i: number) => {
    if (index && i >= index) return acc;
    return acc + minutesFromPrevious;
  }, 0);
  return parseFloat(totalTime.toFixed(2));
};

const pricingColumns = [
  {
    field: 'mileageKm',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'duration',
    minWidth: 200,
    flex: 1.5,
  },
  {
    field: 'cost',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'pricePerRide',
    minWidth: 100,
    flex: 1,
  },
];

interface Props {
  formInstance: UseFormReturn<LineDetailsSchemaType>;
  scheduleValues: CorporateCharterScheduleSchemaType;
}

function PricingTable({ formInstance, scheduleValues }: Props) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const currency = useOperatorCurrency();
  const distanceUnit = useOperatorDistanceUnit();
  const { setValue, control, clearErrors } = formInstance;

  const pricingTableRows = useMemo(() => {
    return (
      <TableRow>
        <TableCell
          sx={{
            fontWeight: 500,
          }}
        >
          {/* // TODO: Replace with <ControlledField /> */}
          <TextField
            value={
              scheduleValues
                ? Math.round(getTotalDistance(scheduleValues, distanceUnit) * 100) / 100
                : ''
            }
            type='number'
            disabled
          />
        </TableCell>
        <TableCell
          sx={{
            fontWeight: 500,
          }}
        >
          {/* // TODO: Replace with <ControlledField /> */}
          <TextField
            value={scheduleValues ? getMinutesFromPrevious(scheduleValues) : ''}
            type='number'
            disabled
          />
        </TableCell>
        <TableCell
          sx={{
            fontWeight: 500,
          }}
        >
          <ControlledField
            control={control}
            name='ridePricePerKmEur'
            fieldProps={{
              InputProps: {
                onChange: (e) => {
                  if (e.target.value === '') {
                    setValue('ridePricePerKmEur', 0);
                    setValue('ridePriceExclVat', 0);
                    return;
                  }

                  if (!Number(e.target.value)) return;

                  clearErrors(['ridePricePerKmEur', 'ridePriceExclVat']);

                  setValue('ridePricePerKmEur', parseFloat(e.target.value));
                  setValue(
                    'ridePriceExclVat',
                    Math.round(
                      Number(e.target.value) * getTotalDistance(scheduleValues, distanceUnit) * 100
                    ) / 100
                  );
                },
              },
            }}
          />
        </TableCell>
        <TableCell
          sx={{
            fontWeight: 500,
          }}
        >
          <ControlledField
            control={control}
            name='ridePriceExclVat'
            fieldProps={{
              InputProps: {
                onChange: (e) => {
                  if (e.target.value === '') {
                    setValue('ridePricePerKmEur', 0);
                    setValue('ridePriceExclVat', 0);
                    return;
                  }

                  if (!Number(e.target.value)) return;

                  clearErrors(['ridePricePerKmEur', 'ridePriceExclVat']);

                  setValue('ridePriceExclVat', parseFloat(e.target.value));
                  setValue(
                    'ridePricePerKmEur',
                    Math.round(
                      (Number(e.target.value) / getTotalDistance(scheduleValues, distanceUnit)) *
                        100
                    ) / 100
                  );
                },
              },
            }}
          />
        </TableCell>
      </TableRow>
    );
  }, [control, scheduleValues, setValue, clearErrors, distanceUnit]);

  return (
    <>
      <Typography
        sx={{
          color: colors.uiBlue600,
          fontSize: '18px',
          mb: '24px',
          fw: 600,
        }}
      >
        {t('CorporateCharterView.steps.lineDetails.pricing.title')}
      </Typography>
      <Paper
        sx={{
          width: '100%',
          background: 'transparent',
          overflowX: 'auto !important',
        }}
      >
        <Paper elevation={3}>
          <Table
            sx={{
              overflow: 'auto',
              background: 'white',
              minWidth: '600px',
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: colors.primaryBlue5,
                }}
              >
                {pricingColumns.map(({ field }) => {
                  return (
                    <TableCell
                      key={field}
                      sx={{
                        fontWeight: 600,
                        borderBottom: 'none',
                        color: colors.gray2,
                      }}
                    >
                      <span>
                        {t(`CorporateCharterView.steps.lineDetails.pricing.table.header.${field}`, {
                          currency,
                          distanceUnit: distanceUnit.toLowerCase(),
                        })}
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>{pricingTableRows}</TableBody>
          </Table>
        </Paper>
      </Paper>
    </>
  );
}

export default PricingTable;
